// src/pages/AllianceManagement/AllianceTechnologyTab.js

import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../../context/AuthContext';
import axiosInstance from '../../utils/axiosInstance';
import { API_URL } from '../../utils/runtimeConfig';
import './css/AllianceTechnologyTab.css';
import { AppButton } from '../../components/ui';

import NationSelector from '../../components/NationSelector';
import AllianceSelector from '../../components/AllianceSelector';

const apiUrl = API_URL;

// A helper function to create the default technology structure as STRINGS
// so the user can type freely in each input (including emptying it).
function createDefaultTechnology() {
  return {
    production: {
      foodProduction: { level: '' },
      oilProduction: { level: '' },
      steelProduction: { level: '' },
      mineralProduction: { level: '' },
      foodGather: { level: '' },
      oilGather: { level: '' },
      steelGather: { level: '' },
      mineralGather: { level: '' },
      civilEngineering: { level: '' },
      researchFocus: { level: '' }
    },
    war: {
      meleeHp: { level: '' },
      midRangeHp: { level: '' },
      longRangeHp: { level: '' },
      meleeDefence: { level: '' },
      midRangeDefence: { level: '' },
      longRangeDefence: { level: '' },
      meleeAttack: { level: '' },
      midRangeAttack: { level: '' },
      longRangeAttack: { level: '' }
    },
    other: {
      allianceAssistance: { level: '' },
      rallyOrganization: { level: '' },
      teamExpansion: { level: '' },
      taxCollection: { level: '' },
      helpTime: { level: '' },
      plantFlags: { level: '' }
    }
  };
}

const AllianceTechnologyTab = () => {
  const { user } = useContext(AuthContext);

  // For picking nation + alliance
  const [nations, setNations] = useState([]);
  const [selectedNation, setSelectedNation] = useState(null);
  const [alliances, setAlliances] = useState([]);
  const [selectedAlliance, setSelectedAlliance] = useState(null);

  // The alliance technology object in state (strings for level fields)
  const [technology, setTechnology] = useState(createDefaultTechnology());

  // Track which category is currently displayed: production, war, or other
  const [activeCategory, setActiveCategory] = useState('production');

  // --------------------------------------------------------------------------
  // 1) On mount, set up the user’s nation, fetch superUser nations, etc.
  // --------------------------------------------------------------------------
  useEffect(() => {
    if (!user) return;

    if (user.role === 'superUser') {
      fetchAllNations();
    }
    // If user.nation is just a string (ID), fetch the doc
    if (typeof user.nation === 'string') {
      fetchNationById(user.nation);
    }
    // If it's already an object, set it
    else if (user.nation && user.nation._id) {
      setSelectedNation(user.nation);
    }
  }, [user]);

  const fetchAllNations = async () => {
    try {
      const res = await axiosInstance.get(`${apiUrl}/nations`);
      setNations(res.data);
    } catch (err) {
      console.error('Error fetching nations:', err);
    }
  };

  const fetchNationById = async (nationId) => {
    try {
      const res = await axiosInstance.get(`${apiUrl}/nations/${nationId}`);
      setSelectedNation(res.data);
    } catch (error) {
      console.error('Error fetching nation for user:', error);
    }
  };

  // --------------------------------------------------------------------------
  // 2) Fetch alliances whenever selectedNation changes
  // --------------------------------------------------------------------------
  useEffect(() => {
    if (selectedNation?._id) {
      fetchAlliances(selectedNation._id);
    }
  }, [selectedNation]);

  const fetchAlliances = async (nationId) => {
    try {
      const res = await axiosInstance.get(`${apiUrl}/nations/${nationId}/alliances`);
      setAlliances(res.data);
    } catch (err) {
      console.error('Error fetching alliances:', err);
    }
  };

  // --------------------------------------------------------------------------
  // 3) Whenever an alliance is selected, load its technology from the backend
  //    and reset local state so we don’t keep old alliance data
  // --------------------------------------------------------------------------
  const fetchAllianceTechnology = useCallback(async (allianceId) => {
    try {
      // For example: GET /alliances/:id
      const res = await axiosInstance.get(`${apiUrl}/alliances/${allianceId}`);
      if (res.data && res.data.technology) {
        // Merge the backend data with our default structure
        mergeFetchedTechnology(res.data.technology);
      } else {
        // If no technology is found, we've already set the defaults
        // so do nothing
      }
    } catch (err) {
      console.error('Error loading alliance technology:', err);
    }
  }, []);

  useEffect(() => {
    if (selectedAlliance?._id) {
      // Reset to defaults, then fill in data from the server
      setTechnology(createDefaultTechnology());
      fetchAllianceTechnology(selectedAlliance._id);
    }
  }, [fetchAllianceTechnology, selectedAlliance]);

  // Merge the fetched data into our local state so fields that are missing
  // remain empty strings, and existing fields become the stored value.
  const mergeFetchedTechnology = (fetched) => {
    const newTech = createDefaultTechnology();

    for (const category of Object.keys(newTech)) {
      if (fetched[category]) {
        for (const techKey of Object.keys(newTech[category])) {
          // If fetched[category][techKey] exists, use that level
          if (fetched[category][techKey]) {
            // Convert it to a string so the user can edit freely
            newTech[category][techKey].level =
              fetched[category][techKey].level !== undefined
                ? String(fetched[category][techKey].level)
                : '';
          }
        }
      }
    }
    setTechnology(newTech);
  };

  // --------------------------------------------------------------------------
  // 4) Update local state on input change
  // --------------------------------------------------------------------------
  // We store these as strings in local state so user can clear out "0"
  // or type partial numbers. We'll parse them on save.
  const handleTechLevelChange = (category, techKey, newValue) => {
    setTechnology((prev) => ({
      ...prev,
      [category]: {
        ...prev[category],
        [techKey]: {
          ...prev[category][techKey],
          level: newValue // keep as string
        }
      }
    }));
  };

  // --------------------------------------------------------------------------
  // 5) Render form inputs for the selected category
  // --------------------------------------------------------------------------
  // Use type="text" so user can remove 0 or type partial numbers. 
  // value can be empty or any string. 
  // We'll parse into Number on save.
  const renderTechnologyInputs = () => {
    if (!technology[activeCategory]) return null;

    const techEntries = Object.entries(technology[activeCategory]);
    return techEntries.map(([techKey, techObj]) => (
      <div key={techKey} className="am-tech-input-row">
        <label>{techKey}</label>
        <input
          type="text"
          value={techObj.level ?? ''} // might be empty
          onChange={(e) => handleTechLevelChange(activeCategory, techKey, e.target.value)}
        />
      </div>
    ));
  };

  // --------------------------------------------------------------------------
  // 6) Save the updated technology to the backend
  // --------------------------------------------------------------------------
  // We'll parse each string into a Number. If it’s empty or invalid, store 0.
  const handleSaveTechnology = async () => {
    if (!selectedAlliance) return;

    try {
      // Build a numeric version of our technology object
      const numericTech = convertStringsToNumbers(technology);

      await axiosInstance.put(`${apiUrl}/alliances/${selectedAlliance._id}/technology`, {
        // The backend expects { technology: { ... } }
        technology: numericTech
      });

      alert('Alliance technology updated successfully!');
    } catch (error) {
      console.error('Error saving technology:', error);
      alert('Failed to save technology.');
    }
  };

  // Convert the entire "technology" tree from string to number.
  // If a field is empty or not a valid number, we default to 0.
  const convertStringsToNumbers = (techObj) => {
    const result = createDefaultTechnology(); // same shape

    for (const category in techObj) {
      for (const techKey in techObj[category]) {
        const strVal = techObj[category][techKey].level;
        const parsed = parseInt(strVal, 10);
        // If user typed something invalid or empty, default to 0
        const finalVal = isNaN(parsed) ? 0 : parsed;

        // Copy into result
        if (!result[category]) {
          result[category] = {};
        }
        result[category][techKey].level = finalVal;
      }
    }
    return result;
  };

  if (user?.role === 'user') {
    return <p>You do not have permission to manage alliance technology.</p>;
  }

  return (
    <div className="am-tech-tab">
      <h2>Alliance Technology</h2>

      {/* SuperUser => pick any nation; nationAdmin => only alliances from their one nation */}
      {user?.role === 'superUser' && (
        <NationSelector
          nations={nations}
          selectedNation={selectedNation}
          setSelectedNation={setSelectedNation}
        />
      )}

      {/* Show alliance dropdown (both for superUser & nationAdmin) */}
      <AllianceSelector
        alliances={alliances}
        selectedAlliance={selectedAlliance}
        setSelectedAlliance={setSelectedAlliance}
      />

      {selectedAlliance && (
        <>
          <h3>
            Editing Tech for {selectedAlliance.name} ({selectedAlliance.acronym})
          </h3>

          {/* Category sub menu (production, war, other) */}
          <div className="am-tech-category-menu">
            <AppButton
              kind={activeCategory === 'production' ? 'primary' : 'secondary'}
              className={activeCategory === 'production' ? 'am-active' : ''}
              onClick={() => setActiveCategory('production')}
            >
              Production
            </AppButton>
            <AppButton
              kind={activeCategory === 'war' ? 'primary' : 'secondary'}
              className={activeCategory === 'war' ? 'am-active' : ''}
              onClick={() => setActiveCategory('war')}
            >
              War
            </AppButton>
            <AppButton
              kind={activeCategory === 'other' ? 'primary' : 'secondary'}
              className={activeCategory === 'other' ? 'am-active' : ''}
              onClick={() => setActiveCategory('other')}
            >
              Other
            </AppButton>
          </div>

          {/* Mobile dropdown for categories */}
          <div className="am-tech-category-dropdown">
            <select
              value={activeCategory}
              onChange={(e) => setActiveCategory(e.target.value)}
            >
              <option value="production">Production</option>
              <option value="war">War</option>
              <option value="other">Other</option>
            </select>
          </div>

          {/* Render the relevant input fields */}
          <div className="am-tech-inputs">{renderTechnologyInputs()}</div>

          <AppButton className="am-tech-save-button" onClick={handleSaveTechnology}>Save Technology</AppButton>
        </>
      )}
    </div>
  );
};

export default AllianceTechnologyTab;
